import { isNumber } from '@turf/turf';
import {
  ECoordinateFormat,
  TCoordinatesFormat,
} from 'DesignSystem/Formik/CoordinatesInputField/CoordinatesInputField';
import { IRawMaterial } from 'FeatureModules/ComponentChain/types/component.types';
import {
  ITableSettings,
  TValidationResult,
} from 'FeatureModules/DataImport/types/dataImport.types';
import {
  EDatasetColumn,
  TPlotGeometryDatasetColumn,
} from 'FeatureModules/DataImport/types/dataset.types';
import { LandOwnershipLabels } from 'FeatureModules/Site/utils/site.utils';
import { isInteger } from 'formik';
import isEqual from 'lodash/isEqual';
import { parse } from 'wellknown';
import {
  IExcelTableContext,
  TValidatorFunction,
} from '../Components/DataImportTable/excelTable.types';
import { VALIDATION_MESSAGE_EMPTY } from './dataImport.constants';
import { isValueEmpty } from './dataImport.utils';

const LNG_COORDINATE_FORMAT_REGEX = [
  /^[-+]?([1-9]?[0-9]|1[0-7][0-9]|180)(\.\d+)?°?$/, // DD: Decimal Degrees with optional degree symbol
  /^[-+]?([1-9]?[0-9]|1[0-7][0-9]|180)°\s*([0-5]?[0-9])'\s*([0-5]?[0-9](\\.\\d+)?)"?\s*[EW]?$/, // DMS: Degrees, Minutes, Seconds with optional N/S/E/W
  /^[-+]?([1-9]?[0-9]|1[0-7][0-9]|180)(\.\d+)?°?\s*\d{1,2}("|\s*")?\s*[EW]?$/, // DDS: Decimal Degrees with up to 9 decimal places and optional N/S/E/W
];

const LAT_COORDINATE_FORMAT_REGEX = [
  /^[-+]?([1-8]?[0-9]|90)(\.\d+)?°?$/, // DD: Decimal Degrees with optional degree symbol
  /^[-+]?([1-8]?[0-9]|90)°\s*([0-5]?[0-9])'\s*([0-5]?[0-9](\\.\\d+)?)"?\s*[NS]?$/, // DMS: Degrees, Minutes, Seconds with optional N/S/E/W
  /^[-+]?([1-8]?[0-9]|90)(\.\d+)?°?\s*\d{1,2}("|\s*")?\s*[NS]?$/, // DDS: Decimal Degrees with up to 9 decimal places and optional N/S/E/W
];

// Case insensitive regex patterns for WKT geometries
export const WKT_GEOMETRY_REGEX = [
  // POLYGON: supports polygons with optional holes (multiple rings)
  // Example: POLYGON ((30 10, 40 40, 20 40, 10 20, 30 10))
  // Example with hole: POLYGON ((35 10, 45 45, 15 40, 10 20, 35 10), (20 30, 35 35, 30 20, 20 30))
  /^POLYGON\s*\(\s*\(\s*(-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*,\s*)*-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*\)(?:\s*,\s*\(\s*(-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*,\s*)*-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*\))*\s*\)$/i,
  // MULTIPOLYGON: supports multiple polygons, each with optional holes
  // Example: MULTIPOLYGON (((30 20, 45 40, 10 40, 30 20)), ((15 5, 40 10, 10 20, 5 10, 15 5)))
  // Example with hole: MULTIPOLYGON (((40 40, 20 45, 45 30, 40 40)), ((20 35, 10 30, 10 10, 30 5, 45 20, 20 35), (30 20, 20 15, 20 25, 30 20)))
  /^MULTIPOLYGON\s*\(\s*\(\s*\(\s*(-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*,\s*)*-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*\)(?:\s*,\s*\(\s*(-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*,\s*)*-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*\))*\s*\)(?:\s*,\s*\(\s*\(\s*(-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*,\s*)*-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*\)(?:\s*,\s*\(\s*(-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*,\s*)*-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*\))*\s*\))*\s*\)$/i,
  // POINT: simple point geometry
  /^POINT\s*\(\s*-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?\s*\)$/i,
];

export const getFirstAndLastPointsFromWKT = (value?: string | null): [number, number][][] => {
  if (!value || value.toLowerCase().startsWith('point')) {
    return [];
  }

  let polygonStrings: string[] = [];
  if (value.toLowerCase().startsWith('multipolygon')) {
    polygonStrings = value
      .toLowerCase()
      .replace('multipolygon', '')
      .replace(')), ((', ')),((')
      .trim()
      .split(')),((');
  }

  if (value.toLowerCase().startsWith('polygon')) {
    polygonStrings = value
      .toLowerCase()
      .replace('polygon', '')
      .replace('), (', '),(')
      .trim()
      .split('),(');
  }

  const polygonsCoordinateStrings = polygonStrings.map(str =>
    str.replaceAll('(', '').replaceAll(')', '').trim()
  );

  const polygonsCoordinatePairs = polygonsCoordinateStrings.map(str =>
    str.split(',').map(coord => {
      const parts = coord.trim().split(' ');
      return [Number(parts[0]), Number(parts[1])] as [number, number];
    })
  );

  // Return the first and last point of each polygon ring
  return polygonsCoordinatePairs.map(polygon => [polygon[0], polygon[polygon.length - 1]]);
};

export const getOpenFirstAndLastPointsFromWKT = (value?: string | null): [number, number][][] => {
  // Invalid or empty WKT
  if (!value || !parse(value)) {
    return [];
  }

  const trimmedValue = value.trim().toLowerCase();
  if (trimmedValue.startsWith('point')) {
    return [];
  }

  const firstAndLastPoints = getFirstAndLastPointsFromWKT(trimmedValue);
  return firstAndLastPoints.filter(polygon => !isEqual(polygon[0], polygon[polygon.length - 1]));
};

export const areCoordinatesValid = (
  coordinates: string,
  format: TPlotGeometryDatasetColumn | TCoordinatesFormat = ECoordinateFormat.COORDINATES_LAT_LNG
): boolean => {
  // Possible supported separators: comma, space
  const splitted =
    coordinates.includes(' ') && !coordinates.includes(',')
      ? coordinates.trim().split(' ')
      : coordinates.trim().replaceAll(' ', '').split(',');

  if (splitted.length !== 2) {
    return false;
  }

  const [lat, lng] =
    format === ECoordinateFormat.COORDINATES_LAT_LNG ? splitted : splitted.reverse();
  const lngMatch = LNG_COORDINATE_FORMAT_REGEX.some(regex => lng.match(regex));
  const latMatch = LAT_COORDINATE_FORMAT_REGEX.some(regex => lat.match(regex));

  if (!latMatch || !lngMatch) {
    return false;
  }

  return true;
};

export const noValidation: TValidatorFunction = () => ({
  isValid: true,
  validationMessage: undefined,
});

export const validateUniqueness: TValidatorFunction = (value, allColumnValues) => {
  if (isValueEmpty(value)) {
    return {
      isValid: false,
      validationMessage: VALIDATION_MESSAGE_EMPTY,
    };
  }

  if (allColumnValues.filter(columnValue => columnValue === value).length > 1) {
    return {
      isValid: false,
      validationMessage:
        'This value is not unique. Please verify that each row has a unique value.',
    };
  }

  return {
    isValid: true,
    validationMessage: undefined,
  };
};

export const validateNotEmpty: TValidatorFunction = value => {
  const valid = !isValueEmpty(value);
  return {
    isValid: valid,
    validationMessage: valid ? undefined : VALIDATION_MESSAGE_EMPTY,
  };
};

export const validateGeometry: TValidatorFunction = (value, allColumnValues, context) => {
  if (!value || isValueEmpty(value)) {
    return {
      isValid: false,
      validationMessage: VALIDATION_MESSAGE_EMPTY,
    };
  }

  let format: TPlotGeometryDatasetColumn | TCoordinatesFormat = EDatasetColumn.GEOMETRY_WKT;
  if (context?.settings?.columns?.geometry?.format) {
    format = context?.settings?.columns.geometry.format;
  } else {
    // @deprecated only used by the old data import
    format =
      context?.settings?.coordinatesVersion === 'lnglat'
        ? ECoordinateFormat.COORDINATES_LNG_LAT
        : ECoordinateFormat.COORDINATES_LAT_LNG;
  }

  if (format === EDatasetColumn.GEOMETRY_WKT) {
    const valid = WKT_GEOMETRY_REGEX.some(regex => regex.test(value.trim()));
    return {
      isValid: valid,
      validationMessage: valid
        ? undefined
        : 'Data cannot be read. Please make sure to use valid WKT. We support points, polygons, and multipolygons.',
    };
  }

  const valid = areCoordinatesValid(value, format);

  return {
    isValid: valid,
    validationMessage: valid
      ? undefined
      : `Location needs to be in format of ${
          format === ECoordinateFormat.COORDINATES_LNG_LAT ? 'Lng, Lat' : 'Lat, Lng'
        }. Two numbers separated by a comma or space while the latitude needs to be in range of -90 to +90 and the longitude in range of -180 to +180.`,
  };
};

export const createPositiveFloatValidator: (
  errorMessage: string,
  required?: boolean
) => TValidatorFunction =
  (errorMessage, required = false) =>
  value => {
    if (required && isValueEmpty(value))
      return {
        isValid: false,
        validationMessage: VALIDATION_MESSAGE_EMPTY,
      };

    if (!required && isValueEmpty(value)) {
      return {
        isValid: true,
        validationMessage: undefined,
        value: undefined,
      };
    }

    if (value === undefined)
      return {
        isValid: undefined,
      };

    const valid = value !== undefined && isNumber(value) && parseFloat(value) >= 0;
    return {
      value: valid ? parseFloat(value) : value,
      isValid: valid,
      validationMessage: valid ? undefined : errorMessage,
    };
  };

export const createPositiveIntegerValidator: (
  errorMessage: string,
  required?: boolean
) => TValidatorFunction =
  (errorMessage, required = false) =>
  value => {
    if (required && isValueEmpty(value))
      return {
        isValid: false,
        validationMessage: VALIDATION_MESSAGE_EMPTY,
      };

    if (!required && isValueEmpty(value)) {
      return {
        isValid: true,
        validationMessage: undefined,
        value: undefined,
      };
    }

    if (value === undefined)
      return {
        isValid: undefined,
      };

    const valid = value !== undefined && isInteger(value) && parseInt(value) >= 0;
    return {
      value: valid ? parseInt(value) : value,
      isValid: valid,
      validationMessage: valid ? undefined : errorMessage,
    };
  };

export const validateNotEmptyAndCrop = (
  value: string | undefined,
  allColumnValues: (string | undefined)[],
  context?: IExcelTableContext & { settings: ITableSettings; rawMaterials: IRawMaterial[] }
): TValidationResult => {
  if (isValueEmpty(value)) {
    return {
      isValid: false,
      validationMessage: VALIDATION_MESSAGE_EMPTY,
    };
  }
  return validateCrop(value, allColumnValues, context);
};

export const validateCrop = (
  value: string | undefined,
  allColumnValues: (string | undefined)[],
  context?: IExcelTableContext & {
    settings: ITableSettings;
    rawMaterials: IRawMaterial[];
    selectMultipleRawMaterials?: boolean;
  }
): TValidationResult => {
  if (!value || isValueEmpty(value)) {
    return {
      isValid: true,
    };
  }

  if (!context?.rawMaterials?.length) {
    return {
      isValid: false,
      validationMessage: 'No raw materials available. Please wait for the list to load.',
    };
  }

  const materialTitles = context?.rawMaterials.map(material => material.title.toLowerCase());

  const isValid = value
    .split(',')
    .every(inputValue => materialTitles.includes(inputValue.trim().toLowerCase()));

  if (!isValid) {
    return {
      isValid: false,
      validationMessage: `${
        context?.selectMultipleRawMaterials
          ? 'One or multiple raw material types are'
          : 'This raw material type is'
      } not yet part of our list. Select an existing type from list, or request a new one.`,
    };
  }

  return {
    isValid: true,
    validationMessage: undefined,
  };
};

export const validateLandOwnership = (value: string | undefined): TValidationResult => {
  if (!value && isValueEmpty(value)) {
    return {
      isValid: true,
    };
  }

  const ownershipTitles = Object.values(LandOwnershipLabels).map(option => option.toLowerCase());

  const isValid = value
    ?.split(',')
    .every(inputValue => ownershipTitles.includes(inputValue.trim().toLowerCase()));

  if (!isValid) {
    return {
      isValid: false,
      validationMessage:
        'This land ownership type is not yet part of our list. Please select an existing type from list',
    };
  }

  return {
    isValid: true,
    validationMessage: undefined,
  };
};

export const validateVerificationNumber: TValidatorFunction = value => {
  // verificationNumber: String should have at least 5 characters
  if (isValueEmpty(value)) {
    return {
      isValid: false,
      validationMessage: VALIDATION_MESSAGE_EMPTY,
    };
  }

  const isValid = typeof value === 'string' && value.length >= 5;

  return {
    isValid,
    validationMessage: isValid
      ? undefined
      : 'Verification number should have at least 5 characters.',
  };
};

export const validateReferenceNumber: TValidatorFunction = value => {
  // referenceNumber: String should follow format YearCountry10RandomChars (e.g. 25DE12346ABCDE)
  if (isValueEmpty(value)) {
    return {
      isValid: false,
      validationMessage: VALIDATION_MESSAGE_EMPTY,
    };
  }

  if (typeof value !== 'string') {
    return {
      isValid: false,
      validationMessage: 'Reference number must be a string.',
    };
  }

  // Check if the reference number follows the pattern: 2 digits (year) + 2 letters (country) + 10 alphanumeric characters
  const referenceNumberPattern = /^[0-9]{2}[A-Z]{2}[A-Z0-9]{10}$/;
  const isValid = referenceNumberPattern.test(value.toUpperCase()) && value.length === 14;

  return {
    isValid,
    validationMessage: isValid
      ? undefined
      : 'Reference number should follow the format YearCountry10Chars (e.g. 25DE12346ABCDE)',
  };
};
